import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LayoutDashboard, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useBooking } from "@/components/booking/BookingDialog";

const LINKS = [
  { to: "/programs", label: "Programs" },
  { to: "/#timetable", label: "Timetable" },
  { to: "/coaching", label: "Coaching" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { open: openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinkClass = ({ isActive }) =>
    `text-sm uppercase tracking-wider transition-colors ${isActive ? "text-gold" : "text-cream/80 hover:text-gold"}`;

  return (
    <header
      className={`safe-top fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-void/85 backdrop-blur-xl border-b border-gold/20" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16 sm:h-20">
        <Link to="/" className="flex items-center gap-3 shrink-0 min-w-0" data-testid="navbar-logo-link" aria-label="Elysium Training Club home">
          <img src="/logo/E-grad.svg" alt="" className="h-9 w-9 sm:h-11 sm:w-11 shrink-0" />
          <span className="font-anton uppercase tracking-[0.08em] text-cream text-lg sm:text-2xl leading-none whitespace-nowrap">
            Elysium <span className="text-gold">Training Club</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} className={navLinkClass} data-testid={`nav-link-${link.label.toLowerCase()}`}>
              {link.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Button
                variant="outline"
                className="rounded-none border-gold text-gold hover:bg-gold hover:text-void gap-2"
                onClick={() => navigate("/dashboard")}
                data-testid="navbar-dashboard-button"
              >
                <LayoutDashboard className="h-4 w-4" /> Dashboard
              </Button>
              <Button
                variant="ghost"
                className="rounded-none text-cream/70 hover:text-gold gap-2"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                data-testid="navbar-logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider"
                onClick={() => openBooking()}
                data-testid="navbar-book-button"
              >
                Book Now
              </Button>
            </>
          )}
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <button className="lg:hidden text-cream h-11 w-11 -mr-2 flex items-center justify-center hover:text-gold" aria-label="Open menu" data-testid="navbar-mobile-menu-trigger">
              <Menu className="h-7 w-7" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-void border-l border-gold/20 w-[85vw] safe-top safe-bottom">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <div className="flex items-center gap-2.5 mb-8 mt-2">
              <img
                src="/logo/elysium-emblem-combined.png"
                alt="Elysium Training Club"
                className="h-28 w-28"
              />
            </div>
            <div className="flex flex-col gap-5">
              {LINKS.map((link) => (
                <SheetClose asChild key={link.to}>
                  <NavLink to={link.to} className={navLinkClass} data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}>
                    {link.label}
                  </NavLink>
                </SheetClose>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-line flex flex-col gap-3">
              {user ? (
                <>
                  <SheetClose asChild>
                    <Button className="rounded-none bg-gold text-void hover:bg-gold-hover w-full" onClick={() => navigate("/dashboard")} data-testid="mobile-dashboard-button">
                      Dashboard
                    </Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button
                      variant="outline"
                      className="rounded-none border-line text-cream/80 w-full"
                      onClick={async () => {
                        await logout();
                        navigate("/");
                      }}
                      data-testid="mobile-logout-button"
                    >
                      Log Out
                    </Button>
                  </SheetClose>
                </>
              ) : (
                <>
                  <SheetClose asChild>
                    <Button className="rounded-none bg-gold text-void hover:bg-gold-hover w-full font-bold uppercase" onClick={() => openBooking()} data-testid="mobile-book-button">
                      Book Now
                    </Button>
                  </SheetClose>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
}
